/**
 * Tracks Service
 */

import { getApiClient } from '../client';
import type {
  Track,
  TrackSearchParams,
  TrackSearchResponse,
  AnalyzeTracksRequest,
  AnalyzeTracksResponse,
  LikedTracksParams,
  LikedTracksResponse,
  AudioFeaturesWithMetadata,
  BatchAudioFeaturesResponse,
} from '../types';

export const trackService = {
  /**
   * Search Spotify catalog with filters
   */
  async search(params: TrackSearchParams): Promise<TrackSearchResponse> {
    return getApiClient().get<TrackSearchResponse>('/tracks/search', params as unknown as Record<string, unknown>);
  },

  /**
   * Get detailed track information
   * @param trackId - Spotify track ID
   */
  async get(trackId: string): Promise<Track> {
    return getApiClient().get<Track>(`/tracks/${encodeURIComponent(trackId)}`);
  },

  /**
   * Analyze tracks and get audio features
   * Features are automatically cached
   */
  async analyze(request: AnalyzeTracksRequest): Promise<AnalyzeTracksResponse> {
    return getApiClient().post<AnalyzeTracksResponse>('/tracks/analyze', request);
  },

  /**
   * Get user's liked songs (saved tracks)
   */
  async getLiked(params?: LikedTracksParams): Promise<LikedTracksResponse> {
    return getApiClient().get<LikedTracksResponse>('/tracks/liked', params as Record<string, unknown>);
  },

  /**
   * Add tracks to Liked Songs
   */
  async likeTracks(trackIds: string[]): Promise<{ message: string }> {
    return getApiClient().put<{ message: string }>('/tracks/liked', { ids: trackIds });
  },

  /**
   * Remove tracks from Liked Songs
   */
  async unlikeTracks(trackIds: string[]): Promise<{ message: string }> {
    return getApiClient().delete<{ message: string }>('/tracks/liked', { ids: trackIds });
  },

  /**
   * Check if tracks are liked (batch, max 50)
   */
  async areLiked(trackIds: string[]): Promise<{ results: Array<{ id: string; isLiked: boolean }> }> {
    return getApiClient().get<{ results: Array<{ id: string; isLiked: boolean }> }>('/tracks/liked/contains', {
      ids: trackIds.join(','),
    });
  },
};

export const audioFeaturesService = {
  /**
   * Get cached audio features for a single track
   * @param trackId - Spotify track ID
   */
  async get(trackId: string): Promise<AudioFeaturesWithMetadata> {
    return getApiClient().get<AudioFeaturesWithMetadata>(
      `/audio-features/${encodeURIComponent(trackId)}`
    );
  },

  /**
   * Get audio features for multiple tracks (batch request)
   * @param ids - Array of track IDs (max 100)
   */
  async getBatch(ids: string[]): Promise<BatchAudioFeaturesResponse> {
    return getApiClient().post<BatchAudioFeaturesResponse>('/audio-features', {
      trackIds: ids,
    });
  },
};
